// hooks/useCardViewTracker.ts
import { useEffect, useRef } from 'react';
import {
  API_CATEGORY_TYPES,
  CUSTOM_ANALYTICS_EVENT_NAMES,
  getAnalyticPageNameByCategory,
  getAnalyticsUserDetails,
  LOG_THROTTLE_TIME_MS,
  logEvent,
  PlatformNames,
} from '@shalina-app/shared';
import { appInsights } from '../../../applicationInsight';

// 🔁 Shared cache and control
const lastLoggedTimes = new Map<string, number>();
let hasLoggedInitialView = false;

// Fallback-safe idle callback
const safeIdleCallback = (cb: () => void) =>
  typeof requestIdleCallback === 'function' ? requestIdleCallback(cb) : setTimeout(cb, 0);

export const useCardViewTracker = ({
  nid,
  title,
  type,
  userDetails,
}: {
  nid: string;
  title: string;
  type: 'marketing' | 'regular';
  userDetails: any;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const latestUserRef = useRef(userDetails);

  // Keep latest user details
  useEffect(() => {
    latestUserRef.current = userDetails;
  }, [userDetails]);

  useEffect(() => {
    if (!ref.current || !nid) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const now = Date.now();
          const lastSeen = lastLoggedTimes.get(nid);

          if (!lastSeen || now - lastSeen > LOG_THROTTLE_TIME_MS) {
            lastLoggedTimes.set(nid, now);

            if (!hasLoggedInitialView) {
              hasLoggedInitialView = true;
            }

            const userData = getAnalyticsUserDetails(latestUserRef.current);
            if (userData && !userData?.emailId) return;

            const eventPayload = {
              userEmail: userData?.emailId,
              userMobile: userData?.mobile,
              userName: userData?.name,
              userQualification: userData?.qualification,
              userInterest: userData?.areaOfInterest,
              userSpecialization: userData?.specialization,
              userCountryOfPractice: userData?.countryOfPractice,
              userPreferredLanguage: userData?.preferredLanguage,
              contentId: nid,
              contentTitle: title || '',
              moduleName: getAnalyticPageNameByCategory(API_CATEGORY_TYPES.FEATURED_CONTENT) || '',
              platform: PlatformNames.WEB,
            };

            safeIdleCallback(() => {
              console.log('LOG:: event payload is ', eventPayload);
              logEvent(
                appInsights,
                'custom',
                CUSTOM_ANALYTICS_EVENT_NAMES.FEATURED_CONTENT_VIEWS,
                eventPayload
              );
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [nid, title, type]);

  return ref;
};

// 🧼 Manual reset for edge cases (exposed from module scope)
export const resetCardViewTracker = () => {
  lastLoggedTimes.clear();
  hasLoggedInitialView = false;
};
